import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { raporBul, sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtTRY, fmtNum } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';
import { lokasyonDestekli, lokasyonParam } from './subeSuzgeci';
import { KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';

interface Satir {
  gun_no: number;
  saat: string;
  fis_sayisi: number;
  ciro: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  sube?: string | null;
}

/**
 * Gun etiketleri ON YUZDE uretiliyor, DB'de degil: `to_char`'in gun adi ciktisi
 * sunucunun `lc_time` ayarina baglidir ve kiraciyla birlikte kayabilir. ds
 * `gun_no` (ISO 8601: 1=Pazartesi ... 7=Pazar) donduruyor; Turkce burada garanti.
 */
export const GUN_ADLARI = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;

/**
 * Hucre yogunlugu. 🔴 Payda MAKSIMUM hucre, toplam DEGIL: isi haritasinin isi
 * "bu hucre en yogun hucrenin ne kadari" sorusudur. Toplama bolmek 7x24=168
 * hucrede her degeri ~%0,6'ya sikistirir ve harita tek renge duserdi.
 *
 * Sifir ciro SEFFAF birakilir (0,08 taban verilmez): "hic satis olmayan saat"
 * ile "cok az satis olan saat" gorsel olarak AYRILMALI - marketin kapali oldugu
 * saatler haritanin en bilgilendirici kismi.
 */
export function yogunluk(ciro: number, enBuyuk: number): number {
  if (enBuyuk <= 0 || ciro <= 0) return 0;
  return Math.min(1, ciro / enBuyuk);
}

/**
 * Saat ekseni VERIDE GECEN saatlerden turer, sabit 0-23 listesinden DEGIL.
 * Gizova'da olculdu (2026-08): 18 saat dolu, 6'si tamamen bos. Sabit 24 kolon
 * cizmek haritanin dortte birini kalici olarak bos birakir ve dar ekranda dolu
 * kisim okunamayacak kadar sikisirdi.
 */
export function saatEkseni(satirlar: Satir[]): string[] {
  return Array.from(new Set(satirlar.map((s) => String(s.saat)))).sort();
}

export function IsiHaritasiBlok({ reports, donem, sube = null }: Props) {
  const rapor = raporBul(reports, 42, 'Saat × Gün Dağılımı');
  const id = sourceIdBul(reports, 42, 'Saat × Gün Dağılımı');
  const destekli = lokasyonDestekli(rapor);
  const params = {
    '@BASTARIH': donem.bas,
    '@BITTARIH': donem.bit,
    ...lokasyonParam(sube, destekli),
  };

  const q = useQuery({
    queryKey: ['sa-isi', id, params],
    queryFn: () => queryDataSource<Satir>({ sourceId: id!, parameters: params }),
    enabled: !!id,
    throwOnError: false,
  });

  const { satirlar, saatler, enBuyuk, haritada } = useMemo(() => {
    const rows = (q.data?.data ?? []).map((r) => ({
      gun_no: Number(r.gun_no),
      saat: String(r.saat),
      fis_sayisi: Number(r.fis_sayisi ?? 0),
      ciro: Number(r.ciro ?? 0),
    }));
    const anahtar = (g: number, s: string) => `${g}|${s}`;
    const map = new Map<string, Satir>();
    for (const r of rows) map.set(anahtar(r.gun_no, r.saat), r);
    return {
      satirlar: rows,
      saatler: saatEkseni(rows),
      enBuyuk: rows.reduce((m, r) => Math.max(m, r.ciro), 0),
      haritada: map,
    };
  }, [q.data]);

  if (!id) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz
  if (q.isLoading)
    return (
      <BlokIskeleti
        testid="blok-isi-iskelet"
        baslik="Saat × gün yoğunluğu"
        altBaslik="Hangi gün, hangi saatte satış yoğun"
        yukseklik={280}
        satir={7}
      />
    );
  if (satirlar.length === 0) return null; // bos durum kurali

  return (
    <div data-testid="blok-isi">
      <SectionCard
        title="Saat × gün yoğunluğu"
        subtitle={
          <>
            Renk koyuluğu ciroyu gösterir — en yoğun saate göre
            <KapsamNotu sube={sube} destekli={destekli} />
          </>
        }
      >
        {/* Dar ekranda 18 kolon sigmaz; harita KENDI icinde yatay kayar
            (sayfa govdesi asla yatay kaymamali). */}
        <div className="overflow-x-auto">
          <table className="border-separate" style={{ borderSpacing: 2 }} data-testid="isi-tablo">
            <thead>
              <tr>
                <th className="text-[11px] text-ink-400 font-normal pr-2 text-right" />
                {saatler.map((s) => (
                  <th key={s} className="text-[10px] text-ink-400 font-normal tnum" style={{ minWidth: 26 }}>
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GUN_ADLARI.map((ad, i) => {
                const gunNo = i + 1;
                return (
                  <tr key={ad} data-testid={`isi-gun-${ad}`}>
                    <td className="text-[11px] text-ink-500 pr-2 text-right whitespace-nowrap">{ad}</td>
                    {saatler.map((s) => {
                      const h = haritada.get(`${gunNo}|${s}`);
                      const ciro = h?.ciro ?? 0;
                      const y = yogunluk(ciro, enBuyuk);
                      return (
                        <td
                          key={s}
                          data-testid={`isi-${ad}-${s}`}
                          data-yogunluk={y.toFixed(2)}
                          // Baslik hem fare ustunde hem ekran okuyucuda konusur:
                          // renk tek basina bir olcu DEGILDIR (renk korlugu).
                          title={`${ad} ${s}:00 — ${fmtTRY(ciro)} · ${fmtNum(h?.fis_sayisi ?? 0)} fiş`}
                          style={{
                            width: 26,
                            height: 22,
                            borderRadius: 3,
                            background:
                              y === 0 ? 'var(--color-border-light, #eef1f5)' : `rgba(37, 99, 235, ${0.12 + y * 0.78})`,
                          }}
                        />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
